import { useEffect, useRef } from 'react';
import { Message } from './Message.js';
import { MessageSquare } from 'lucide-react';
import type { Message as MessageType } from '@/types/message.js';
import { LoadingState } from '@/components/common/LoadingState.js';

interface MessageListProps {
  messages?: MessageType[];
  loading?: boolean;
  loadingHistory?: boolean;
}

export function MessageList({ messages = [], loading = false, loadingHistory = false }: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messageCountRef = useRef(0);

  // Ensure messages is always an array
  const safeMessages = Array.isArray(messages) ? messages : [];

  // Auto-scroll to bottom only when message count changes
  useEffect(() => {
    const currentCount = safeMessages.length;
    if (currentCount > messageCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    messageCountRef.current = currentCount;
  }, [safeMessages.length]); // Only depend on length, not the array itself

  // Show loading state when history is being fetched
  if (loadingHistory) {
    return <LoadingState message="Loading conversation..." />;
  }

  if (safeMessages.length === 0 && !loading) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold text-foreground mb-3">How can I help you?</h2>
          <p className="text-muted-foreground leading-relaxed">
            I'm your AI assistant. Ask me anything or tell me what you need help with.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      <div ref={scrollRef} className="max-w-4xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {safeMessages.map((message) => {
          if (!message || !message.id) {
            return null;
          }

          return <Message key={message.id} message={message} />;
        })}

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground animate-fade-in my-2 px-8">
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
              <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
              <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
            </div>
            <span className="text-xs">Thinking...</span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
